'use client';

import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { DateObject, formatDate } from "@/lib/interfaces/settings";
import { AuthContextProps, User } from "@/lib/interfaces/user";
import { loginAction, logoutAction, refreshTokenAction } from "@/app/actions/auth";
import moment from "moment";

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export function useAuth(): AuthContextProps {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null);
    // accessToken is kept in context for backward compatibility with profile components
    // that pass it to updateProfile(); it is never populated — updateProfile() proxies
    // through /api/profile which reads the httpOnly cookie server-side.
    const accessToken = '';
    const [loading, setLoading] = useState(true);

    // Bootstrap auth state from the httpOnly cookie via /api/me.
    useEffect(() => {
        fetch('/api/me')
            .then(r => r.ok ? r.json() : null)
            .then(async (data: { user: User; expiry_date: string; remember: boolean } | null) => {
                if (!data) return;

                const expiryTime = moment(data.expiry_date).subtract(10, 'minutes');
                if (moment().isAfter(expiryTime)) {
                    try {
                        await refreshTokenAction();
                    } catch {
                        return; // Refresh failed — treat as logged out
                    }
                }

                setUser(data.user);
            })
            .catch(() => {
                // Non-fatal — user is not authenticated
            })
            .finally(() => setLoading(false));
    }, []);

    const login = async (email: string, password: string, remember_me: boolean = false) => {
        const u = await loginAction(email, password, remember_me);
        setUser(u);
    };

    const register = async (
        email: string,
        password: string,
        date: DateObject,
        policy: boolean,
        marketing_mail: boolean,
    ) => {
        const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email,
                password,
                password_confirmation: password,
                policy,
                birthdate: formatDate(date),
                marketing_mail,
            }),
        });
        if (!res.ok) {
            const data = await res.json();
            throw data;
        }
    };

    const logout = () => {
        setUser(null);
        setLoading(true);
        logoutAction()
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    const refresh = async (): Promise<void> => {
        try {
            await refreshTokenAction();
            // Re-sync client state after the server-side cookie is updated
            const response = await fetch('/api/me');
            const data = response.ok ? await response.json() : null;
            setUser(data?.user ?? null);
        } catch {
            // Refresh failures mean the session is no longer usable.
            setUser(null);
        }
    };

    return (
        <AuthContext.Provider value={{ user, accessToken, login, register, logout, refresh, loading }}>
            {children}
        </AuthContext.Provider>
    );
}
